import React, { useState, useContext, useEffect, useMemo } from "react";
import { NotificationManager } from "react-notifications";
import { FilterContext } from "../FilterContextProvider";
import SmallInput from "../Components/SmallInput";
import Input from "../Components/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import useApi from "../Api/Api";
import Button from "../Components/Button";
import InventoryLayout from "./InventoryLayout";
import useProducts from "../hooks/useProducts";
import useAllProducts from "../hooks/useAllProducts";
import cn from "classnames";
import useSalesSummary from "../hooks/useSalesSummary";

const AdjustInventory = () => {
  const { filterData } = useContext(FilterContext);
  const api = useApi();
  const [errorMessage, setErrorMessage] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [alterSalesObj, setAlterSalesObj] = useState({});
  const [seeAll, setSeeAll] = useState(false);
  const {
    productsToRender: allSalesProducts,
    getAllTimeNonShowSales,
    resultsLoading,
  } = useSalesSummary("active");

  const {
    getProducts,
    products,
    pagy,
    currentPage,
    setCurrentPage,
    resetPagination,
    setSearchFilter,
  } = useProducts(true);

  const { allProducts, setAllProdsSearchFilter } = useAllProducts(true);

  const displayedProducts = useMemo(() => {
    return seeAll ? allProducts : products;
  }, [seeAll, allProducts, products]);

  const [chosenDate, setChosenDate] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const handleChosenDate = (e) => {
    setChosenDate(e.target.value);
    setDateError(false); // Clear date error when user selects a date
  };

  const [searchTimeout, setSearchTimeout] = useState();

  const handleSearchChange = (e) => {
    setSearchTimeout(e.target.value);
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setAllProdsSearchFilter(searchTimeout);
      setSearchFilter(searchTimeout);
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [searchTimeout, setSearchFilter, setAllProdsSearchFilter]);

  const reset = () => {
    setChosenDate("");
    setAlterSalesObj({});
    setSearchTimeout("");
    setDateError(false);
  };

  const handlePageChange = (e) => {
    setSeeAll(false);
    setCurrentPage(e.target.value);
  };

  displayedProducts.forEach((prod) => {
    prod.reporting_type = "adjust";
    prod.product_id = prod.id;
    prod.product_name = prod.name;
    prod.product_category = prod.category?.name;
    prod.price = prod.retail_price;
    prod.date = chosenDate;
    if (prod.size_other) {
      prod.product_size = prod.size_other;
    } else if (prod?.size?.name) {
      prod.product_size = prod.size.name;
    } else if (!prod.size) {
      prod.product_size = null;
    }
    const allTimeSaleCopy = allSalesProducts.find(({ id }) => prod.id === id);
    prod.event_count = allTimeSaleCopy?.event_count;
    prod.current = allTimeSaleCopy?.current;
    prod.sold = allTimeSaleCopy?.sold;
  });

  const handleInputChange = ({ target: { name, value } }, product) => {
    setAlterSalesObj({
      ...alterSalesObj,
      [product.id]: {
        ...product,
        [name]: value,
      },
    });
  };

  const handleSeeAll = () => {
    setSeeAll(!seeAll);
  };

  const handlePostSales = (event) => {
    event.preventDefault();

    if (!chosenDate || chosenDate.trim() === '') {
      setErrorMessage({ error: 'Please select a date before submitting adjustments.' });
      setDateError(true);
      return;
    }
    
    // Clear date error if validation passes
    setDateError(false);
    
    Promise.all(
      Object.values(alterSalesObj).map((sale) => {
        delete sale.sold;
        delete sale.current;
        if (
          (sale.purchases?.trim() === "" || !sale.purchases) &&
          (sale.discount_quantity?.trim() === "" || !sale.discount_quantity) &&
          (sale.damage_quantity?.trim() === "" || !sale.damage_quantity) &&
          (sale.mail_order?.trim() === "" || !sale.mail_order) &&
          (sale.ctm_comp?.trim() === "" || !sale.ctm_comp) &&
          (sale.management_comp?.trim() === "" || !sale.management_comp) &&
          (sale.band_comp_quantity?.trim() === "" ||
            !sale.band_comp_quantity) &&
          (sale.inventory_adjust?.trim() === "" || !sale.inventory_adjust)
        ) {
        } else {
          sale.date = chosenDate;
          return api.post(`/sales`, { sale });
        }
        return null;
      })
    )
      .then((response) => {
        NotificationManager.success(
          "Adjustment successfully created",
          "Success",
          2000
        );
        getProducts(currentPage);
        setAlterSalesObj({});
        setErrorMessage(false);
        setDateError(false);
        getAllTimeNonShowSales();
        return response;
      })
      .catch((error) => {
        setErrorMessage(error.response.data);
      })
      .finally(() => {});
  };

  const renderSelectArtistMessage = () => {
    return (
      <div className="m-8 py-12 pl-4">
        Please select an artist from the filter in order to make an adjustment
      </div>
    );
  };

  const columns = [
    { label: "PURCHASES", value: "purchases" },
    { label: "DISCOUNT", value: "discount_quantity" },
    { label: "DAMAGE", value: "damage_quantity" },
    { label: "MAIL ORDER", value: "mail_order" },
    { label: "TM COMP", value: "ctm_comp" },
    { label: "MGMT COMP", value: "management_comp" },
    { label: "ARTIST COMP", value: "band_comp_quantity" },
    { label: "INVENTORY ADJUST", value: "inventory_adjust" },
  ];

  let count = 0;

  for (let i = 1; i < displayedProducts.length; i++) {
    if (displayedProducts[i - 1].name !== displayedProducts[i].name) {
      count++;
    }
    displayedProducts[i].productNumber = count;
  }

  const renderAdjustTable = () => {
    return (
      <div>
        <div className="mx-10 pt-8">
          <div className="text-black text-lg mb-4 font-medium">
            Inventory Adjust
          </div>
          <div className="flex mb-4 text-sm">
            <div className="absolute mt-2 ml-2 text-black">
              <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
            </div>
            <div className="w-1/4 mr-4">
              <Input
                onChange={handleSearchChange}
                value={searchTimeout}
                placeholder="Search for products"
                bigPaddingLeft
              />
            </div>
            <div className="w-1/4 mr-8">
              <input
                value={chosenDate}
                type="date"
                name="date"
                id="date"
                className={`h-9 pl-2 rounded-md border w-64 font-medium w-full ${
                  dateError ? 'border-red-500 border-2' : ''
                }`}
                onChange={handleChosenDate}
                required
              ></input>
            </div>
            {(searchTimeout ||
              chosenDate ||
              Object.values(alterSalesObj).length > 0) && (
              <button
                onClick={reset}
                className="text-blue-500 font-normal italic text-sm pl-2"
              >
                Reset
              </button>
            )}
            <div className="grid justify-items-center">
              {errorMessage?.error && (
                <div className="ml-8 mt-4 text-red-500 pb-2">
                  {errorMessage.error}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end -mt-16 mr-2 pb-8">
            <Button
              className={
                Object.values(alterSalesObj).length === 0
                  ? "button button-disabled -mt-6"
                  : "button -mt-6"
              }
              onClick={handlePostSales}
              text="Submit"
              disabled={Object.values(alterSalesObj).length === 0}
            ></Button>
          </div>
        </div>
        {displayedProducts.length > 0 ? (
          <div>
            <form className="flex w-full text-xs">
              <table className="w-1/3 mt-4">
                <thead className="text-xs font-light sticky top-0 bg-white">
                  <tr className="text-left border-b-2 h-11 text-center">
                    <td className="w-1/3 text-left pl-6">PRODUCT</td>
                    <td className="w-1/12">PER SHOW</td>
                    <td className="w-1/12">TOTAL CURRENT</td>
                  </tr>
                </thead>
                <tbody>
                  {displayedProducts
                    // .filter((each) => each.active)
                    .map((product, i) => {
                      const productToRender =
                        alterSalesObj[product.id] || product;
                      return (
                        <tr
                          key={productToRender.id}
                          className={cn("border-b text-sm h-11 text-center", {
                            "bg-yellow-50": alterSalesObj[product.id],
                            "bg-gray-100": displayedProducts[i].productNumber % 2 === 1,
                          })}
                        >
                          {productToRender.product_size ? (
                            <td className="text-left pl-6">
                              {productToRender.name} -{" "}
                              {productToRender.product_size}
                            </td>
                          ) : (
                            <td className="text-left pl-6">
                              {productToRender.name}
                            </td>
                          )}
                          <td
                            className={
                              productToRender.sold < 0
                                ? " bg-red-300 rounded-md"
                                : ""
                            }
                            id={`per_show_${i}`}
                          >
                            {productToRender.event_count
                              ? (
                                  productToRender.sold /
                                  productToRender.event_count
                                )?.toFixed(2)
                              : ""}
                          </td>
                          <td
                            id={`current_${i}`}
                            className={cn("", {
                              "bg-yellow-200 rounded-md":
                                productToRender.current /
                                  (productToRender.sold /
                                    productToRender.event_count) <
                                  3 && product.current > 0,
                              "bg-red-300 rounded-md":
                                productToRender.current < 0,
                            })}
                          >
                            {productToRender.current}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
              {columns.map((attribute) => {
                return (
                  <table className="w-1/12 text-center mt-4">
                    <thead className="text-xs font-light sticky top-0 bg-white">
                      <tr className="text-left border-b-2 h-11 text-center">
                        <td>{attribute.label}</td>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedProducts.map((product, i) => {
                        const productToRender =
                          alterSalesObj[product.id] || product;
                        return (
                          <tr
                            key={productToRender.id}
                            className={cn("border-b text-sm h-11 text-center", {
                              "bg-yellow-50": alterSalesObj[product.id],
                              "bg-gray-100":
                              displayedProducts[i].productNumber % 2 === 1,
                            })}
                          >
                            <td>
                              <SmallInput
                                name={attribute.value}
                                id={`${attribute.value}_${i}`}
                                onChange={(event) =>
                                  handleInputChange(event, productToRender)
                                }
                                value={productToRender[attribute.value]}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>{" "}
                  </table>
                );
              })}
            </form>
          </div>
        ) : (
          <div className="mx-12 py-16">
            {resultsLoading ? (
              <>Results are loading...</>
            ) : (
              <> No products have been added for this artist yet</>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <InventoryLayout>
      <div className="text-black bg-gray-100 pt-2">
        <div className="mt-8 mx-8 text-black bg-white rounded-lg border">
          {filterData.artist_id
            ? renderAdjustTable()
            : renderSelectArtistMessage()}
        </div>
        {filterData.artist_id && (
          <>
            <footer className="text-sm mt-4 mx-8 flex justify-between">
              <div className="ml-1 mr-8">
                Showing <span className="font-bold">{seeAll ? 1 : pagy.from} </span>to
                <span className="font-bold"> {seeAll ? pagy.count : pagy.to} </span>of
                <span className="font-bold"> {pagy.count} </span>results
              </div>
              {seeAll ? (
                <div className="mr-8"> Showing All Results </div>
              ) : (
                <div className="mr-8">
                  Page {pagy.page} of {pagy.last}
                </div>
              )}

              <div className="flex -mt-2 mb-4">
              <span>
                  <Button
                    value={0}
                    className={seeAll ? "button-white-disabled button-md" : "button button-white button-md"}
                    onClick={handleSeeAll}
                    text="See All"
                    disabled={false}
                  ></Button>
                </span>
                <span>
                  <Button
                    className={
                      pagy.page === 1
                        ? "button-white-disabled button-sm"
                        : "button button-white button-sm"
                    }
                    onClick={() => {
                      setSeeAll(false);
                      resetPagination();
                    }}
                    text="First"
                    disabled={pagy.page === 1}
                  ></Button>
                </span>
                <span className="mx-1">
                  <Button
                    value={pagy.prev}
                    className={
                      !pagy.prev
                        ? "button-white-disabled button-sm"
                        : "button button-white button-sm"
                    }
                    onClick={handlePageChange}
                    text="Prev"
                    disabled={!pagy.prev}
                  ></Button>
                </span>
                <span>
                  <Button
                    value={pagy.next}
                    className={
                      !pagy.next || seeAll
                        ? "button-white-disabled button-sm"
                        : "button button-white button-sm"
                    }
                    onClick={handlePageChange}
                    text="Next"
                    disabled={!pagy.next || seeAll}
                  ></Button>
                </span>
                <span className="ml-1">
                  <Button
                    value={pagy.last}
                    className={
                      pagy.page === pagy.last || seeAll
                        ? "button-white-disabled button-sm"
                        : "button button-white button-sm"
                    }
                    onClick={handlePageChange}
                    text="Last"
                    disabled={pagy.page === pagy.last || seeAll}
                  ></Button>
                </span>
              </div>
            </footer>
          </>
        )}
      </div>
    </InventoryLayout>
  );
};

export default AdjustInventory;
